import React, { useEffect, useState } from 'react';
import {
    FormControl,
    FormLabel,
    FormGroup,
    RadioGroup,
    FormControlLabel,
    Radio,
    makeStyles,
    FormHelperText,
} from '@material-ui/core';
import { useFormikContext, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import type { FormField } from '../interfaces';
import {
    findCamundaProperty,
    findCamundaPropertyValue,
    parseTranslatedJsonArrayOfStrings,
} from '../utils';

const useStyles = makeStyles((theme) => ({
    requiredIndicator: {
        '& .MuiTypography-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    error: {
        color: theme.palette.error.main,
    },
}));

export const FormikRadioGroup = ({
    id: fieldId,
    label: fieldLabel,
    isRequired,
    isReadOnly,
    callback,
    field,
    locale,
}: {
    id?: string;
    label?: string;
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
    locale: string;
    callback?: (...params) => void;
}) => {
    const { t } = useTranslation();
    const { setFieldValue } = useFormikContext();
    const [formikField, formikFieldMeta] = useField<string | null>(
        fieldId as string,
    );
    const [customRadioOptions, setCustomRadioOptions] = useState<Array<{
        id: string;
        label: string;
    }> | null>(null);
    const classes = useStyles();

    const verticalProperty = findCamundaProperty('vertical', field?.properties);
    const customRadioOptionField = findCamundaPropertyValue(
        'items',
        field?.properties,
    );

    const handleChange = (event) => {
        if (customRadioOptions) {
            setFieldValue(fieldId as string, event.target.value);
            if (callback) {
                callback(event.target.value);
            }
            return;
        }

        setFieldValue(fieldId as string, event.target.value === 'true');
        if (callback) {
            callback(event.target.value === 'true');
        }
    };

    useEffect(() => {
        if (!customRadioOptionField) return;
        setCustomRadioOptions(
            parseTranslatedJsonArrayOfStrings(customRadioOptionField, locale),
        );
    }, [customRadioOptionField, locale]);

    return (
        <FormControl
            className={classes.requiredIndicator}
            error={Boolean(formikFieldMeta?.error) && formikFieldMeta?.touched}
            component="fieldset"
            disabled={isReadOnly}
            required={isRequired}
        >
            <FormLabel component="legend">{fieldLabel}</FormLabel>
            <FormGroup>
                <RadioGroup
                    aria-label={fieldLabel}
                    name={fieldId}
                    value={String(formikField.value)}
                    onChange={handleChange}
                    row={!verticalProperty}
                >
                    {customRadioOptions ? (
                        customRadioOptions?.map(({ id, label }) => (
                            <FormControlLabel
                                key={String(id) || String(label)}
                                value={String(id) || String(label)}
                                control={<Radio color="primary" />}
                                label={label}
                                disabled={isReadOnly}
                            />
                        ))
                    ) : (
                        <>
                            <FormControlLabel
                                value="true"
                                control={<Radio color="primary" />}
                                label={t('project.formFields.radioGroup.yes')}
                                disabled={isReadOnly}
                            />
                            <FormControlLabel
                                value="false"
                                control={<Radio color="primary" />}
                                label={t('project.formFields.radioGroup.no')}
                                disabled={isReadOnly}
                            />{' '}
                        </>
                    )}
                </RadioGroup>
                {formikFieldMeta?.touched && formikFieldMeta?.error && (
                    <FormHelperText
                        id="component-error-text"
                        className={classes.error}
                    >
                        {formikFieldMeta?.error}
                    </FormHelperText>
                )}
            </FormGroup>
        </FormControl>
    );
};
